<template>
  <div class="auth-image" :class="{ 'auth-image--rounded': rounded }" :style="boxStyle">
    <img
      v-if="url"
      :src="url"
      :alt="alt"
      draggable="false"
      :style="{ objectFit: contain ? 'contain' : 'cover' }"
      @load="onLoad"
    />
    <div v-else class="auth-image__state" :title="failed ? errorText : ''">
      <v-progress-circular v-if="loading" indeterminate :size="spinnerSize" width="2" color="#2d8659" />
      <v-icon v-else-if="failed" color="grey">{{ forbidden ? 'mdi-lock-outline' : 'mdi-image-off-outline' }}</v-icon>
      <v-icon v-else color="grey lighten-1">mdi-image-outline</v-icon>
    </div>
    <slot />
  </div>
</template>

<script>
// A photo served by GET /files/:id(/thumb): fetched with the JWT, shown from an object URL (photoCache)
import { acquirePhoto, releasePhoto } from "./photoCache";
import { photoErrorMessage } from "./api";

export default {
  name: "AuthImage",
  props: {
    fileId: { type: [Number, String], default: null },
    // false = the ~480 px thumbnail, true = the photo itself
    full: { type: Boolean, default: false },
    alt: { type: String, default: "Fotografie" },
    width: { type: [Number, String], default: null },
    height: { type: [Number, String], default: null },
    contain: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    // fetch only when it scrolls into view
    lazy: { type: Boolean, default: true },
  },
  data() {
    return { url: null, loading: false, failed: false, forbidden: false, errorText: "", visible: !this.lazy, held: null };
  },
  computed: {
    variant() {
      return this.full ? "full" : "thumb";
    },
    boxStyle() {
      const size = v => (v == null ? null : typeof v === "number" || /^\d+$/.test(v) ? `${v}px` : v);
      return { width: size(this.width), height: size(this.height) };
    },
    spinnerSize() {
      const h = Number(this.height);
      return h && h < 48 ? 16 : 24;
    },
  },
  watch: {
    fileId() {
      this.load();
    },
    full() {
      this.load();
    },
  },
  mounted() {
    if (this.lazy && typeof IntersectionObserver === "function") {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries.some(e => e.isIntersecting)) {
            this.visible = true;
            this.observer.disconnect();
            this.observer = null;
            this.load();
          }
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$el);
    } else {
      this.visible = true;
      this.load();
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    this.release();
  },
  methods: {
    release() {
      if (this.held) releasePhoto(this.held.id, this.held.variant);
      this.held = null;
    },
    async load() {
      if (!this.visible) return;
      const id = this.fileId;
      const variant = this.variant;
      if (this.held && this.held.id === id && this.held.variant === variant) return;
      this.release();
      this.url = null;
      this.failed = false;
      this.forbidden = false;
      if (id == null || id === "") return;
      const held = { id, variant };
      this.held = held;
      this.loading = true;
      try {
        const url = await acquirePhoto(id, variant);
        if (this.held === held) this.url = url;
      } catch (error) {
        if (this.held !== held) return;
        this.held = null; // nothing was cached for a failed fetch
        this.failed = true;
        this.forbidden = !!(error.response && error.response.status === 403);
        this.errorText = photoErrorMessage(error, "Fotografia nu a putut fi afișată.");
        this.$emit("error", error);
      } finally {
        if (this.held === held || !this.held) this.loading = false;
      }
    },
    onLoad(event) {
      this.$emit("load", { url: this.url, width: event.target.naturalWidth, height: event.target.naturalHeight });
    },
  },
};
</script>

<style scoped>
.auth-image {
  position: relative;
  overflow: hidden;
  background: #f5f5f5;
  width: 100%;
  height: 100%;
}
.auth-image--rounded {
  border-radius: 8px;
}
.auth-image img {
  display: block;
  width: 100%;
  height: 100%;
  user-select: none;
  -webkit-user-drag: none;
}
.auth-image__state {
  position: absolute;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
